import type { FC } from 'react';
import {
  Archive,
  Database,
  FileText,
  Inbox,
  PenSquare,
  Plus,
  Send,
  Trash2,
  UserCheck,
} from 'lucide-react';
import type { MailboxResponse } from '@antigravity/types';

interface WebmailSidebarProps {
  mailboxes: MailboxResponse[];
  selectedMailbox: MailboxResponse | null;
  onSelectMailbox: (mailbox: MailboxResponse) => void;
  onOpenCompose: () => void;
  unreadCount?: number;
  activeFolder?: string;
  onSelectFolder?: (folder: string) => void;
  onOpenConnectSender?: () => void;
}

export const WebmailSidebar: FC<WebmailSidebarProps> = ({
  mailboxes,
  selectedMailbox,
  onSelectMailbox,
  onOpenCompose,
  unreadCount = 0,
  activeFolder = 'inbox',
  onSelectFolder,
  onOpenConnectSender,
}) => {
  // Format bytes to readable strings
  const formatBytes = (bytes?: number | bigint) => {
    const num = Number(bytes || 0);
    if (num === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(num) / Math.log(k));
    return `${parseFloat((num / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const used = Number(selectedMailbox?.usedBytes || 0);
  const quota = Number(selectedMailbox?.quotaBytes || 1073741824); // default 1GB
  const percentUsed = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;

  return (
    <div className="h-full w-64 bg-slate-900 border-r border-slate-800 flex flex-col justify-between shrink-0">
      <div className="p-4 space-y-4">
        {/* Mailbox Selector */}
        <div>
          <div className="flex items-center justify-between mb-1.5 px-1">
            <label htmlFor="mailbox-select" className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Active Mailbox
            </label>
            {onOpenConnectSender && (
              <button
                type="button"
                onClick={onOpenConnectSender}
                className="text-[11px] font-semibold text-brand-400 hover:text-brand-300 flex items-center gap-1 transition-colors px-1.5 py-0.5 rounded-md hover:bg-brand-500/10 cursor-pointer"
                title="Create New Mailbox or Connect Custom Domain"
              >
                <Plus className="w-3 h-3" />
                <span>+ Connect / New</span>
              </button>
            )}
          </div>
          {mailboxes.length === 0 ? (
            <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-800 text-xs text-slate-400 text-center space-y-2">
              <p>No active mailboxes found</p>
              {onOpenConnectSender && (
                <button
                  type="button"
                  onClick={onOpenConnectSender}
                  className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-[11px] font-semibold shadow transition-all w-full flex items-center justify-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>+ Add Business Mailbox</span>
                </button>
              )}
            </div>
          ) : (
            <div className="relative">
              <select
                id="mailbox-select"
                aria-label="Active Mailbox"
                value={selectedMailbox?.id || ''}
                onChange={(e) => {
                  const mb = mailboxes.find((m) => m.id === e.target.value);
                  if (mb) onSelectMailbox(mb);
                }}
                className="w-full px-3 py-2 rounded-xl bg-slate-800/80 border border-slate-700/80 text-xs font-medium text-white focus:outline-none focus:border-brand-500 appearance-none cursor-pointer truncate"
              >
                {mailboxes.map((mb) => (
                  <option key={mb.id} value={mb.id}>
                    {mb.displayName ? `${mb.displayName} (${mb.emailAddress})` : mb.emailAddress}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedMailbox && (
            <div className="mt-2 px-1 flex items-center justify-between text-[11px] text-slate-400">
              <span className="flex items-center gap-1">
                <UserCheck className="w-3 h-3 text-emerald-400" />
                <span className="text-emerald-400 font-medium">{selectedMailbox.status}</span>
              </span>
              <span className="text-[10px] text-slate-400">Stalwart v0.16</span>
            </div>
          )}
        </div>

        {/* Compose Button */}
        <button
          type="button"
          onClick={onOpenCompose}
          disabled={!selectedMailbox || selectedMailbox.status !== 'ACTIVE'}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold shadow-lg shadow-brand-500/20 transition-all"
        >
          <PenSquare className="w-4 h-4" />
          <span>Compose Email</span>
        </button>

        {/* Folders Navigation */}
        <div className="pt-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 px-1">
            Mail Folders
          </div>
          <nav className="space-y-1">
            {/* Inbox */}
            <button
              type="button"
              onClick={() => onSelectFolder?.('inbox')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                activeFolder === 'inbox'
                  ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <Inbox className="w-4 h-4" />
                <span>Inbox</span>
              </span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-brand-500 text-white text-[10px] font-bold">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Sent */}
            <button
              type="button"
              onClick={() => onSelectFolder?.('sent')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                activeFolder === 'sent'
                  ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <Send className="w-4 h-4" />
                <span>Sent</span>
              </span>
            </button>

            {/* Drafts */}
            <button
              type="button"
              onClick={() => onSelectFolder?.('drafts')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                activeFolder === 'drafts'
                  ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <FileText className="w-4 h-4" />
                <span>Drafts</span>
              </span>
            </button>

            {/* Trash */}
            <button
              type="button"
              onClick={() => onSelectFolder?.('trash')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                activeFolder === 'trash'
                  ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <Trash2 className="w-4 h-4" />
                <span>Trash</span>
              </span>
            </button>

            {/* Archive */}
            <button
              type="button"
              onClick={() => onSelectFolder?.('archive')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                activeFolder === 'archive'
                  ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <Archive className="w-4 h-4" />
                <span>Archive</span>
              </span>
            </button>
          </nav>
        </div>
      </div>

      {/* Storage Quota Widget */}
      <div className="p-4 border-t border-slate-800 bg-slate-900/50">
        <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1.5">
          <span className="flex items-center gap-1.5 font-medium text-slate-300">
            <Database className="w-3.5 h-3.5 text-brand-400" />
            Storage Quota
          </span>
          <span className="font-mono text-[10px]">{percentUsed}%</span>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden mb-1.5">
          <div
            className={`h-full transition-all duration-300 ${
              percentUsed > 90
                ? 'bg-rose-500'
                : percentUsed > 75
                ? 'bg-amber-500'
                : 'bg-brand-500'
            }`}
            style={{ width: `${percentUsed}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
          <span>{formatBytes(used)}</span>
          <span>{formatBytes(quota)}</span>
        </div>
      </div>
    </div>
  );
};
